import React, { useState, useEffect, useRef } from "react";
import useFetch from "shared/hooks/useFetch";
import "styles/components/forms/inputs/countrySelect.css";
import Loader from "shared/components/common/Loader";

type CountryType = {
  name: {
    common: string;
  };
  flags: {
    svg: string;
    png: string;
    alt: string;
  };
};

const CountryOptions = ({
  query,
  selectedCallback,
}: {
  query: string;
  selectedCallback: (selectedOption: { name: string; value: string }) => void;
}) => {
  const [countries, setCountries] = useState<CountryType[]>([]);
  const [svgLoading, setSvgLoading] = useState<boolean>(true);
  const [filteredCountries, setFilteredCountries] = useState<CountryType[]>([]);

  const cache = useRef(new Map());

  // Fetch countries
  const {
    data,
    loading: fetchLoading,
    error,
  } = useFetch<CountryType[]>(
    "https://restcountries.com/v3.1/all?fields=name,flags"
  );

  // Fetch all svgs and store in cache
  useEffect(() => {
    if (data && error == null) {
      const processCountries = async () => {
        try {
          await Promise.all(
            data.map(async (country) => {
              const flagUrl = country.flags.svg;

              if (!cache.current.has(flagUrl)) {
                const svgData = await fetch(flagUrl).then((res) => res.text());
                const dataUri = `data:image/svg+xml;base64,${btoa(svgData)}`;
                cache.current.set(flagUrl, dataUri);
              }
            })
          );

          setCountries(
            data.sort((a, b) => a.name.common.localeCompare(b.name.common))
          );
        } catch (error) {
          console.error(error);
        } finally {
          setSvgLoading(false);
        }
      };

      processCountries();
    }
  }, [data, error]);

  // filter countries
  useEffect(() => {
    const filtered = countries.filter((country) =>
      country.name.common.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredCountries(filtered);
  }, [query, countries]);

  const getFlagSvg = (flagUrl: string) => {
    return cache.current.has(flagUrl) ? cache.current.get(flagUrl) : "";
  };

  const shouldShowTitle = (countryName: string, index: number) => {
    // normalized letters (Åland Islands)
    const collator = new Intl.Collator("en", { sensitivity: "base" });
    const currentFirstLetter = countryName.charAt(0).toUpperCase();
    const previousFirstLetter =
      index > 0
        ? filteredCountries[index - 1].name.common.charAt(0).toUpperCase()
        : null;

    // Compare letters in a locale-aware way
    return (
      !previousFirstLetter ||
      collator.compare(currentFirstLetter, previousFirstLetter) !== 0
    );
  };

  if (fetchLoading || svgLoading) return <Loader />;
  if (error) return <p>Error: {error}</p>;

  return (
    <ul style={{ flex: 1 }}>
      {filteredCountries.map((country, index) => {
        const showTitle = shouldShowTitle(country.name.common, index);
        return (
          <React.Fragment key={country.name.common}>
            {showTitle && (
              <div className="country-group-title">
                <span>{country.name.common.charAt(0).toUpperCase()}</span>
              </div>
            )}
            <CountryOption
              flag={getFlagSvg(country.flags.svg)}
              alt={country.flags.alt || `${country.name.common} flag`}
              name={country.name.common}
              selectedCallback={selectedCallback}
            />
          </React.Fragment>
        );
      })}
    </ul>
  );
};

const CountryOption = ({
  flag,
  name,
  alt,
  selectedCallback,
}: {
  flag: string;
  name: string;
  alt: string;
  selectedCallback: (selectedOption: { name: string; value: string }) => void;
}) => {
  return (
    <li
      onClick={() => selectedCallback({ name, value: flag })}
      className="country-option"
    >
      <img src={flag} alt={alt} />
      <span>{name}</span>
    </li>
  );
};

export default CountryOptions;
